import {
  Modal,
  ModalContent,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react'
import type React from 'react'
import { useState } from 'react'
import BubbleChatIcon from '../../../assets/icons/bubble-chat-stroke-rounded'
import { useContactSupportMutation } from '../../../redux/services/coreApi'

interface NeedHelpProps {
  company: 'pcm' | 'otc'
}

// Human-readable label for the desk the user is contacting, prefixed onto the
// subject so the support mailbox knows which product the request came from.
const contactContext: Record<NeedHelpProps['company'], string> = {
  pcm: 'Capital Management',
  otc: 'Broker desk / OTC',
}

const NeedHelp: React.FC<NeedHelpProps> = ({ company }) => {
  const modal = useDisclosure()
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [contactSupport, { isLoading, isSuccess, isError, reset }] =
    useContactSupportMutation()

  const canSubmit = subject.trim().length > 0 && message.trim().length > 0

  const close = () => {
    modal.onClose()
    setSubject('')
    setMessage('')
    reset()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit || isLoading) return
    try {
      await contactSupport({
        subject: `${contactContext[company]}: ${subject.trim()}`,
        message: message.trim(),
      }).unwrap()
    } catch {
      // Failure is surfaced to the user via the isError branch below.
    }
  }

  return (
    <>
      <div
        onClick={modal.onOpen}
        className="bg-brand-app-menu-hover  hover:bg-brand-app-menu-hover/50 select-none cursor-pointer border border-white/10 border-solid flex gap-2.5 items-center rounded-xl transition-all px-3 py-2.5 mb-2"
      >
        <div className="p-0.5 border border-solid border-white/15 rounded-full">
          <div className="w-9 h-9 rounded-full shrink-0 bg-brand-app-menu flex items-center justify-center">
            <BubbleChatIcon className="w-5 h-5" />
          </div>
        </div>

        <div className="text-xs">
          <p className="font-medium text-black/50">Need help?</p>
          <p className="font-semi text-black">Contact support</p>
        </div>
      </div>

      <Modal isOpen={modal.isOpen} onClose={close}>
        <ModalOverlay bg={'rgba(0, 0, 0,0.85)'} />
        <ModalContent rounded="24px" p="0px" maxW={'600px'} marginX="16px">
          <div className="bg-white dark:bg-brand-dark-card rounded-2xl relative overflow-hidden p-12 pt-10">
            <p className="text-description">{contactContext[company]}</p>
            <p className="font-semi text-lg dark:text-white">
              How can we help you?
            </p>

            {isSuccess ? (
              <div className="mt-8">
                <p className="font-semi dark:text-white">
                  Thanks — your message is on its way.
                </p>
                <p className="text-description mt-1">
                  Our support team will reply to your email in under 1 business
                  day.
                </p>
                <button
                  type="button"
                  onClick={close}
                  className="mt-6 w-full bg-brand-app-menu hover:bg-brand-app-menu-hover transition-all rounded-xl py-3 font-semi dark:text-white"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="support-subject" className="text-description">
                    Subject
                  </label>
                  <input
                    id="support-subject"
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    maxLength={200}
                    placeholder="What do you need help with?"
                    className="rounded-xl bg-brand-app-menu dark:bg-brand-dark-background px-4 py-3 outline-none dark:text-white"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="support-message" className="text-description">
                    Message
                  </label>
                  <textarea
                    id="support-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength={5000}
                    rows={5}
                    placeholder="Describe your question or issue…"
                    className="rounded-xl bg-brand-app-menu dark:bg-brand-dark-background px-4 py-3 outline-none resize-none dark:text-white"
                  />
                </div>

                {isError && (
                  <p className="text-red-500 text-sm">
                    Something went wrong sending your message. Please try again.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={!canSubmit || isLoading}
                  className="w-full bg-brand-blue text-white rounded-xl py-3 font-semi transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Sending…' : 'Send message'}
                </button>

                <p className="text-description text-center">
                  Prefer email? Write us at{' '}
                  <a
                    href="mailto:support@penning.io"
                    className="hover:underline text-blue-600 dark:text-blue-400"
                  >
                    support@penning.io
                  </a>
                </p>
              </form>
            )}
          </div>
        </ModalContent>
      </Modal>
    </>
  )
}

export default NeedHelp
